/**
 * @typedef {import('@/types').EngineSnapshot} EngineSnapshot
 * @typedef {import('@/types').EngineContext} EngineContext
 * @typedef {import('@/types').FilterOption} FilterOption
 * @typedef {import('@/types').GroupedOptions} GroupedOptions
 * @typedef {import('@/types').GroupedSelected} GroupedSelected
 * @typedef {import('@/types').CampusRow} CampusRow
 * @typedef {import('@/types').TermRow} TermRow
 * @typedef {import('@/types').SubjectRow} SubjectRow
 * @typedef {import('@/types').CourseRow} CourseRow
 * @typedef {import('@/types').MonthYearRow} MonthYearRow
 * @typedef {import('@/types').CollegeOptionsData} CollegeOptionsData
 * @typedef {import('@/types').TermOptionsData} TermOptionsData
 * @typedef {import('@/types').CoursesData} CoursesData
 * @typedef {import('@/types').SearchBody} SearchBody
 * @typedef {import('@/types').SearchConfig} SearchConfig
 * @typedef {import('@/types').PhaseMeta} PhaseMeta
 * @typedef {import('@/types').StepRuntime} StepRuntime
 * @typedef {import('@/types').StepMode} StepMode
 * @typedef {import('@/types').ApplyKey} ApplyKey
 * @typedef {import('@/types').SyncNav} SyncNav
 * @typedef {import('@/engine/filter-graph.js').StepId} StepId
 * @typedef {import('@/engine/run-step.js').StepResult} StepResult
 */

/**
 * @param {EngineSnapshot} snapshot
 * @param {string} key
 */
function markSelectedDirty(snapshot, key) {
	if (!snapshot._dirtySelectedKeys) {
		snapshot._dirtySelectedKeys = [];
	}
	if (!snapshot._dirtySelectedKeys.includes(key)) {
		snapshot._dirtySelectedKeys.push(key);
	}
}

/**
 * @param {EngineSnapshot} snapshot
 */
export function initSnapshotDirtyKeys(snapshot) {
	snapshot._dirtySelectedKeys = [];
}

/**
 * @param {FilterOption[]} options
 * @returns {boolean[]}
 */
function mapCatchAllSelection(options) {
	return options.map((option) => {
		return option.hasOwnProperty('type') && (option.type === '*' || option.type === 'first');
	});
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {CampusRow[]} campuses
 */
function applyCampus(snapshot, campuses) {
	snapshot.options.campus = [
		{
			value: '',
			text: 'All Campuses',
			type: '*',
		},
	];
	campuses.forEach((campus) => {
		snapshot.options.campus.push({
			value: campus.campus,
			text: campus.location,
		});
	});
	snapshot.selected.campus = mapCatchAllSelection(snapshot.options.campus);
	snapshot.loading.campus = false;
	markSelectedDirty(snapshot, 'campus');
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {TermRow[]} terms
 * @param {EngineContext} context
 */
function applyTerms(snapshot, terms, context) {
	snapshot.options.term = [];
	terms.forEach((term) => {
		snapshot.options.term.push({
			value: term.term_code,
			text: term.term_description,
		});
	});

	let index = 0;
	let preselected = context.preselectedFiltersConfig?.preselected?.term;
	if (preselected?.term_description && snapshot.options.term.findIndex((term) => term.text === preselected.term_description) !== -1) {
		index = snapshot.options.term.findIndex((term) => term.text === preselected.term_description);
	}
	snapshot.options.term[index].type = 'first';
	snapshot.selected.term = snapshot.options.term.map((option) => {
		return option.hasOwnProperty('type') && option.type === 'first';
	});
	snapshot.loading.term = false;
	markSelectedDirty(snapshot, 'term');
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {Record<string, MonthYearRow[]>} startsOn
 * @param {StepMode} mode
 * @param {EngineContext} context
 */
function applyStartsOn(snapshot, startsOn, mode, context) {
	let monthKey = context.monthKey;

	if (mode === 'initial') {
		Object.keys(startsOn).forEach((startsOnYear) => {
			snapshot.options.startsOn[startsOnYear] = startsOn[startsOnYear].map((monthYear) => {
				return {
					value: monthYear.year + '-' + monthYear.month,
					text: monthKey[monthYear.month],
				};
			});
		});
		Object.keys(startsOn).forEach((startsOnYear) => {
			snapshot.selected.startsOn[startsOnYear] = startsOn[startsOnYear].map(() => false);
		});
	} else {
		/** @type {GroupedOptions} */
		let newStartsOnOptions = {
			0: [
				{
					value: '',
					text: 'All Months',
					type: '*',
				},
			],
		};
		Object.keys(startsOn).forEach((startsOnYear) => {
			newStartsOnOptions[startsOnYear] = startsOn[startsOnYear].map((monthYear) => {
				return {
					value: monthYear.year + '-' + monthYear.month,
					text: monthKey[monthYear.month],
				};
			});
		});
		/** @type {GroupedSelected} */
		let newStartsOnSelectedOptions = {
			0: [true],
		};
		Object.keys(startsOn).forEach((startsOnYear) => {
			newStartsOnSelectedOptions[startsOnYear] = startsOn[startsOnYear].map(() => false);
		});
		snapshot.options.startsOn = newStartsOnOptions;
		snapshot.selected.startsOn = newStartsOnSelectedOptions;
	}

	snapshot.loading.startsOn = false;
	markSelectedDirty(snapshot, 'startsOn');
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {string[]} formats
 * @param {StepMode} mode
 * @param {EngineContext} context
 */
function applyFormat(snapshot, formats, mode, context) {
	let formatKey = context.formatKey;

	if (mode === 'initial') {
		snapshot.options.format.push({
			value: '',
			text: 'All Formats',
			type: '*',
		});
		formats.forEach((format) => {
			snapshot.options.format.push({
				value: format,
				text: formatKey[format],
			});
		});
		snapshot.selected.format = mapCatchAllSelection(snapshot.options.format);
	} else {
		snapshot.options.format = formats.map((format) => {
			return {
				value: format,
				text: formatKey[format],
			};
		});
		snapshot.options.format.unshift({
			value: '',
			text: 'All Formats',
			type: '*',
		});
		snapshot.selected.format = mapCatchAllSelection(snapshot.options.format);
	}

	snapshot.loading.format = false;
	markSelectedDirty(snapshot, 'format');
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {SubjectRow[]} subjects
 * @param {StepMode} mode
 */
function applySubjects(snapshot, subjects, mode) {
	if (mode === 'initial') {
		snapshot.options.subject.push({
			value: '',
			text: 'All Subjects',
			type: '*',
			is_active: true,
		});
		subjects.forEach((subject) => {
			snapshot.options.subject.push({
				value: subject.subject,
				text: subject.subject + ' - ' + subject.subject_description,
				is_active: true,
			});
		});
		snapshot.selected.subject = mapCatchAllSelection(snapshot.options.subject);
	} else {
		snapshot.options.subject = subjects.map((subject) => {
			return {
				value: subject.subject,
				text: subject.subject + ' - ' + subject.subject_description,
			};
		});
		snapshot.options.subject.unshift({
			value: '',
			text: 'All Subjects',
			type: '*',
			is_active: true,
		});
		snapshot.selected.subject = mapCatchAllSelection(snapshot.options.subject);
	}

	snapshot.loading.subject = false;
	markSelectedDirty(snapshot, 'subject');
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {CoursesData} courses
 */
function applyCourses(snapshot, courses) {
	/** @type {GroupedOptions} */
	let newCourseOptions = {
		0: [
			{
				value: '',
				text: 'All Courses',
				type: '*',
			},
		],
	};
	Object.keys(courses).forEach((subjectDescription) => {
		newCourseOptions[subjectDescription] = courses[subjectDescription].map((course) => {
			return {
				value: course.subject + ' ' + course.catalog_number,
				text: course.subject + ' ' + course.catalog_number,
			};
		});
	});
	/** @type {GroupedSelected} */
	let newCourseSelectedOptions = {
		0: [true],
	};
	Object.keys(courses).forEach((subjectDescription) => {
		newCourseSelectedOptions[subjectDescription] = courses[subjectDescription].map(() => false);
	});
	snapshot.options.course = newCourseOptions;
	snapshot.selected.course = newCourseSelectedOptions;
	snapshot.loading.course = false;
	markSelectedDirty(snapshot, 'course');
}

/**
 * @param {EngineSnapshot} snapshot
 */
function applyResetCourse(snapshot) {
	snapshot.options.course = {
		0: [
			{
				value: '',
				text: 'All Courses',
				type: '*',
			},
		],
	};
	snapshot.selected.course = {
		0: [true],
	};
	markSelectedDirty(snapshot, 'course');
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {SearchBody} searchBody
 * @param {SearchConfig} [searchConfig]
 */
function applySearch(snapshot, searchBody, searchConfig) {
	let config = searchConfig ?? { update: true };

	snapshot.results = searchBody.results;
	snapshot.categories = searchBody.categories;
	snapshot.resultsLoading = false;
	snapshot.navLoading = false;
	snapshot.loading.search = false;

	if (config.initial || config.update) {
		snapshot.currentNav = { ...snapshot.categories[0], initial: true };
	}

	return searchBody;
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {SearchBody} searchBody
 * @param {StepRuntime} [runtime]
 */
function applySearchPage(snapshot, searchBody, runtime) {
	snapshot.currentNav = runtime?.category
		? runtime.category
		: { ...snapshot.categories[0], initial: false };
	snapshot.results = searchBody.results;
	snapshot.resultsLoading = false;
	snapshot.loading.search = false;
	return searchBody;
}

/**
 * @param {EngineSnapshot} snapshot - working copy for the active generation
 * @param {StepResult} stepResult
 * @param {EngineContext} context
 */
export function applyStep(snapshot, stepResult, context) {
	let { stepId, data, phaseMeta, planMeta, runtime } = stepResult;
	/** @type {StepMode} */
	let mode = phaseMeta?.mode ?? 'update';

	switch (stepId) {
		case 'collegeOptions': {
			let collegeData = /** @type {CollegeOptionsData} */ (data);
			applyCampus(snapshot, collegeData.campus);
			applyTerms(snapshot, collegeData.terms, context);
			break;
		}
		case 'termOptions': {
			let termData = /** @type {TermOptionsData} */ (data);
			/** @type {ApplyKey[]} */
			let applyKeys = phaseMeta?.apply ?? ['startsOn', 'formats', 'subjects'];
			if (applyKeys.includes('startsOn')) {
				applyStartsOn(snapshot, termData.startsOn, mode, context);
			} else {
				snapshot.loading.startsOn = false;
			}
			if (applyKeys.includes('formats')) {
				applyFormat(snapshot, termData.formats, mode, context);
			} else {
				snapshot.loading.format = false;
			}
			if (applyKeys.includes('subjects')) {
				applySubjects(snapshot, termData.subjects, mode);
			} else {
				snapshot.loading.subject = false;
			}
			break;
		}
		case 'resetCourse':
			applyResetCourse(snapshot);
			break;
		case 'courses':
			applyCourses(snapshot, /** @type {CoursesData} */ (data));
			break;
		case 'search': {
			let searchConfig = phaseMeta?.search ?? planMeta?.search ?? { update: true };
			applySearch(snapshot, /** @type {SearchBody} */ (data), searchConfig);
			break;
		}
		case 'searchPage':
			applySearchPage(snapshot, /** @type {SearchBody} */ (data), runtime);
			break;
		default:
			throw new Error(`Unknown step id: ${stepId}`);
	}
}

/**
 * Mirrors App.vue post-search currentNavResults assignment.
 *
 * @param {EngineSnapshot} snapshot
 * @param {SyncNav} syncNav
 * @param {StepRuntime} [runtime]
 */
export function applySyncNav(snapshot, syncNav, runtime = {}) {
	switch (syncNav) {
		case 'assign':
			snapshot.currentNavResults = snapshot.currentNav;
			break;
		case 'spread':
			snapshot.currentNavResults = { ...snapshot.currentNav };
			break;
		case 'category':
			snapshot.currentNavResults = { .../** @type {import('@/types').NavCategory} */ (runtime.category) };
			break;
		default:
			break;
	}
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {StepId} stepId
 * @param {boolean} loading
 * @param {PhaseMeta} [phaseMeta]
 */
export function setStepLoading(snapshot, stepId, loading, phaseMeta = {}) {
	switch (stepId) {
		case 'collegeOptions':
			snapshot.loading.campus = loading;
			snapshot.loading.term = loading;
			break;
		case 'termOptions': {
			/** @type {ApplyKey[]} */
			let applyKeys = phaseMeta.apply ?? ['startsOn', 'formats', 'subjects'];
			if (applyKeys.includes('startsOn')) {
				snapshot.loading.startsOn = loading;
			}
			if (applyKeys.includes('formats')) {
				snapshot.loading.format = loading;
			}
			if (applyKeys.includes('subjects')) {
				snapshot.loading.subject = loading;
			}
			break;
		}
		case 'courses':
			snapshot.loading.course = loading;
			break;
		case 'search':
			snapshot.resultsLoading = loading;
			snapshot.loading.search = loading;
			if (loading) {
				snapshot.navLoading = true;
			}
			break;
		case 'searchPage':
			snapshot.resultsLoading = loading;
			snapshot.loading.search = loading;
			break;
		default:
			break;
	}
}

/**
 * @param {EngineSnapshot} snapshot
 * @param {PhaseMeta} phaseMeta
 */
export function attachPhaseMeta(snapshot, phaseMeta) {
	snapshot._phaseMeta = phaseMeta;
}

/**
 * @param {EngineSnapshot} snapshot
 */
export function detachPhaseMeta(snapshot) {
	delete snapshot._phaseMeta;
}
